import { BarChart3, TrendingUp, Clock, Shield, Users, Activity, GitCommit } from 'lucide-react';
import type { ActivityPillarDetails } from '../../../types/analysis.types';
import { MetricCard, SectionHeader, DistributionBar, RiskImplicationsPanel } from './shared';
import { safeNum, type PillarDetailsCommonProps } from './shared.utils';

interface Props extends PillarDetailsCommonProps {
  details: ActivityPillarDetails;
}

export const ActivityDetails: React.FC<Props> = ({
  details,
  metricBg,
  trackColor,
  theme,
  score,
}) => {
  const d = details as unknown as Record<string, unknown>;
  const commits30d =
    safeNum(d.commitCount30d ?? d.commitsLast30Days ?? d.commits_last_30_days) ?? 0;
  const commits90d =
    safeNum(d.commitCount90d ?? d.commitsLast90Days ?? d.commits_last_90_days) ?? 0;
  const daysSinceCommit = safeNum(d.daysSinceLastCommit ?? d.days_since_last_commit) ?? 0;
  const contributors =
    safeNum(d.activeContributors30d ?? d.activeContributors ?? d.active_contributors) ?? 0;
  const trend = (d.commitTrend ?? d.commit_trend ?? 'stable') as
    | 'increasing'
    | 'stable'
    | 'declining';

  const trendConfig = {
    increasing: { color: '#10b981', label: 'Increasing', icon: '↑' },
    stable: { color: '#eab308', label: 'Stable', icon: '→' },
    declining: { color: '#ef4444', label: 'Declining', icon: '↓' },
  };
  const trendInfo = trendConfig[trend] || trendConfig.stable;

  const weeklyBreakdown = d.weeklyBreakdown as {
    thisWeek?: number;
    lastWeek?: number;
    threeWeeksPlus?: number;
  } | null;
  const activityDistribution = weeklyBreakdown
    ? [
        { label: 'This week', value: weeklyBreakdown.thisWeek ?? 0, color: '#10b981' },
        { label: 'Last week', value: weeklyBreakdown.lastWeek ?? 0, color: '#22c55e' },
        { label: '3+ weeks', value: weeklyBreakdown.threeWeeksPlus ?? 0, color: '#eab308' },
      ]
    : [
        { label: 'This week', value: Math.round(commits30d / 3), color: '#10b981' },
        { label: 'Last week', value: Math.round(commits30d / 3), color: '#22c55e' },
        { label: '3+ weeks', value: Math.round(commits30d / 3), color: '#eab308' },
      ];
  const activityTotal = activityDistribution.reduce((sum, item) => sum + item.value, 0);

  const getRiskMessage = () => {
    if (daysSinceCommit > 180)
      return 'No commits in 6+ months indicates the project may be abandoned or in maintenance-only mode.';
    if (daysSinceCommit > 90)
      return 'Activity has slowed significantly. This may indicate reduced maintenance priority.';
    if (commits30d < 5) return 'Low commit frequency may slow bug fixes and feature development.';
    if (contributors < 2) return 'Single-contributor projects have higher bus factor risk.';
    return null;
  };

  const riskMessage = getRiskMessage();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Commits (30d)"
          value={commits30d}
          icon={<GitCommit className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Commits (90d)"
          value={commits90d}
          icon={<Activity className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Last Commit"
          value={daysSinceCommit}
          subtext="days ago"
          icon={<Clock className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        <MetricCard
          label="Contributors"
          value={contributors}
          subtext="active (30d)"
          icon={<Users className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
      </div>

      <div>
        <SectionHeader icon={<TrendingUp className="w-4 h-4" />} title="Commit Trend" />
        <div className="rounded-lg p-4" style={{ backgroundColor: metricBg }}>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold" style={{ color: trendInfo.color }}>
              {trendInfo.icon}
            </span>
            <div>
              <div className="text-lg font-semibold" style={{ color: trendInfo.color }}>
                {trendInfo.label}
              </div>
              <div className="text-xs text-secondary">
                Comparing recent activity to historical patterns
              </div>
            </div>
          </div>
        </div>
      </div>

      {activityTotal > 0 && (
        <div>
          <SectionHeader
            icon={<BarChart3 className="w-4 h-4" />}
            title="Recent Activity Distribution"
          />
          <div className="space-y-2">
            {activityDistribution.map((item) => (
              <DistributionBar
                key={item.label}
                label={item.label}
                value={item.value}
                total={activityTotal}
                trackColor={trackColor}
                barColor={item.color}
              />
            ))}
          </div>
        </div>
      )}

      {riskMessage && score < 70 && (
        <div>
          <SectionHeader icon={<Shield className="w-4 h-4" />} title="Risk Implications" />
          <RiskImplicationsPanel message={riskMessage} theme={theme} />
        </div>
      )}
    </div>
  );
};
